import { useEffect, useState } from "react";
import { ModalsKeys } from "global";
import { useModal } from "hooks/useModal";
import { useFieldArray, useForm } from "react-hook-form";
import { Column, ListingForm, RHFAndCustomSettings } from "./types";

export function useListing<
  Data extends Record<string, any>,
  Context extends any = any,
  ColumnType extends string = string,
>(initialData: ListingForm<Data, Context, ColumnType>, props?: RHFAndCustomSettings<Data, Context, ColumnType>) {
  const { handleOpen: onOpenConfirmationModal } = useModal(ModalsKeys.Confirmation);

  const form = useForm<ListingForm<Data, Context, ColumnType>>({
    defaultValues: initialData,
    shouldUnregister: true,
    ...(props ?? {}),
  });

  const [columns, setColumns] = useState<Column<Data, Context, ColumnType>[]>(initialData.headerColumns);

  const {
    fields: tableData,
    append: onAddRow,
    remove: onRemove,
    insert: onInsertRow,
    update: onUpdate,
    move: onMoveRow,
  } = useFieldArray({
    control: form.control,
    name: "bodyFields" as any,
  });

  const handleAddColumn = (column: Column<Data, Context, ColumnType>) => {
    setColumns(prev => [...prev, column]);
  };

  const handleRemoveColumn = (columnIndex: number) => {
    setColumns(prev => prev.filter((_, index) => index !== columnIndex));
  };

  const handleUpdateColumnType = (columnIndex: number, nextValue: ColumnType) => {
    setColumns(prev =>
      prev.map((column, index) => {
        if (index === columnIndex) return { ...column, columnType: nextValue, renderKey: nextValue };
        return column;
      })
    );
  };

  const handleResetColumns = () => {
    setColumns(initialData.headerColumns);
  };

  const handleAddRow = (row: Data) => {
    onAddRow(row);
  };

  const handleInsertRow = (row: Data, index: number) => {
    onInsertRow(index, row);
  };

  const handleUpdateRow = (row: Data, index: number) => {
    onUpdate(index, row);
  };

  const handleMoveRow = (fromIndex: number, toIndex: number) => {
    onMoveRow(fromIndex, toIndex);
  };

  const handleRemoveRow = (index: number) => {
    onOpenConfirmationModal({
      title: "Confirmation",
      message: "Are you sure you want to remove this row?",
      onConfirm: () => onRemove(index),
    });
  };

  useEffect(() => {
    const subscription = form.watch(({ bodyFields }) => {
      if (!bodyFields || typeof props?.onSubscribe !== "function") return;

      const cleanBodyFields = bodyFields.filter((row): row is Data => row !== undefined);

      props?.onSubscribe(cleanBodyFields);
    });

    return subscription.unsubscribe;
  }, [form, props, props?.onSubscribe]);

  return {
    form,
    columns,
    onRemove,
    handleAddRow,
    handleMoveRow,
    handleAddColumn,
    handleRemoveRow,
    handleInsertRow,
    handleUpdateRow,
    handleRemoveColumn,
    handleResetColumns,
    handleUpdateColumnType,
    tableData: tableData as Data[],
  };
}
